import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import PrivacyBar from '/@/components/privacy-bar';
import settings from '/@/core/settings';
import { useAllCatalogProducts } from '/@/hooks/catalog';
import { useEnvironmentRangesFromApi } from '/@/hooks/product-datasheet';
import { useFeaturesForProducts, useMetricsForProducts } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import { useCatalogZones } from '/@/store/catalog/apiProviders';
import { getMetricChoices } from '/@/store/private-data/recommendations/selectors';
import {
    addExtra,
    setCompletePreview,
    setProductDatasheetConfig,
    setProductDatasheetSlug,
    setProductDatasheetType,
    setUser,
    setZone,
} from '/@/store/product-datasheet/actions';
import { useProductDatasheetConfig } from '/@/store/product-datasheet/apiProviders';
import {
    getProductDatasheet,
    getProductDatasheetBrand,
    getProductDatasheetConfig,
    getSelectedIds,
    getSelectedModulesIds,
    getZone,
    getZones,
} from '/@/store/product-datasheet/selectors';
import { getSessionCountry, getUser } from '/@/store/session/selectors';
import { reverse } from '/@/utils/router';
import Select from '/@/v3/components/forms/select';
import Title from '/@/v3/components/title';
import Modules from '../modules';
import ProductList from '../product-list';
import styles from './styles.module.css';

function HookWrapper(Component) {
    return function WrappedComponent(props) {
        let currentZone;

        const productDatasheetConfigsQuery = useProductDatasheetConfig({ page: 1, pageSize: 10000 });
        const productDatasheetConfigs = productDatasheetConfigsQuery.data?.results || [];

        const config = useSelector((state) => getProductDatasheetConfig(state, props, productDatasheetConfigs));
        const brand = useSelector((state) => getProductDatasheetBrand(state, props, productDatasheetConfigs));

        const sessionCountry = useSelector(getSessionCountry);

        const catalogZonesQuery = useCatalogZones({ page: 1, pageSize: 10000, crop: props.type, type: 'catalog' });
        const catalogZones = catalogZonesQuery.data?.results || [];

        const zone = useSelector((state) => getZone(state, props, brand, catalogZones));
        const zones = useSelector((state) => getZones(state, { type: props.type }, brand, catalogZones));

        if (!zone && zones.length > 0) {
            currentZone = zones[0].id;
        } else {
            currentZone = zone?.id;
        }
        const { data: allProducts, isValidating: loading } = useAllCatalogProducts({
            country: sessionCountry.code,
            brand: brand?.name,
            crop: props.type,
            zone: currentZone,
        });
        let products = allProducts?.results || [];

        const { data: environmentRanges, isLoading: environmentRangesLoading } = useEnvironmentRangesFromApi({
            ids: products.map((p) => {
                return p.id;
            }),
            crop: props.type,
        });

        const { data: productsMetrics, isValidating: productsMetricsLoading } = useMetricsForProducts({
            crop: props.type,
            productsId: products.map((p) => {
                return p.id;
            }),
        });
        const { data: productsFeatures, isValidating: productsFeaturesLoading } = useFeaturesForProducts({
            crop: props.type,
            productsId: products.map((p) => {
                return p.id;
            }),
        });

        products = products.map((p) => {
            return {
                ...p,
                environmentRanges: environmentRanges?.[p.id],
                metrics: productsMetrics?.[p.id],
                features: productsFeatures?.[p.id],
            };
        });

        const metrics = useMemo(() => {
            return (
                (!environmentRangesLoading &&
                    !productsMetricsLoading &&
                    !productsFeaturesLoading &&
                    getMetricChoices(products)) ||
                []
            );
        }, [environmentRangesLoading, productsMetricsLoading, productsFeaturesLoading, products]);

        return (
            <Component
                {...props}
                products={products}
                metricChoices={metrics}
                brand={brand}
                config={config}
                loading={
                    loading ||
                    environmentRangesLoading ||
                    productsMetricsLoading ||
                    productsFeaturesLoading ||
                    productDatasheetConfigsQuery.isValidating ||
                    catalogZonesQuery.isValidating
                }
                zones={zones}
                zone={zone}
            />
        );
    };
}

@connect(
    (state, props) => {
        return {
            user: getUser(state),

            productDatasheet: getProductDatasheet(state),
            selectedIds: getSelectedIds(state),
            modulesIds: getSelectedModulesIds(state),
            sessionCountry: getSessionCountry(state),
        };
    },
    (dispatch) =>
        bindActionCreators(
            {
                addExtra,
                setUser,
                setZone,
                setProductDatasheetType,
                setProductDatasheetSlug,
                setProductDatasheetConfig,
                setCompletePreview,
            },
            dispatch
        )
)
@autobind
class ProductDatasheetProductsConfiguration extends Component {
    static contextTypes = {
        history: PropTypes.object,
    };

    state = {
        products: [],
        metricChoices: [],
        selectedProfiles: [],
        loading: true,
    };

    handleProductChange(product) {
        const index = this.props.products.findIndex((p) => p.id === product.id);
        if (index !== -1) {
            this.props.products[index] = product;
        }
    }

    async componentDidMount() {
        this.props.setCompletePreview([]);
        const { zones, zone, setZone, user } = this.props;

        this.props.setUser(user);
        this.props.setProductDatasheetType(this.props.type);
        this.props.setProductDatasheetSlug(this.props.slug);

        // add disclaimer to show in detail
        this.props.addExtra('legalText');
    }

    componentDidUpdate(prevProps) {
        if (prevProps.selectedIds !== this.props.selectedIds) {
            const selectedProfiles = this.props.products.filter((profile) =>
                this.props.selectedIds.includes(profile.id)
            );
            this.setState({ selectedProfiles: selectedProfiles });
        }

        if (prevProps.config !== this.props.config) this.props.setProductDatasheetConfig(this.props.config);
        if (prevProps.zones !== this.props.zones)
            if (!this.props.zone && this.props.zones.length > 0) this.props.setZone(this.props.zones[0].id);
    }

    getItemsAsOptions(items) {
        return items.map((item) => ({
            label: _(item.name),
            value: item.id,
        }));
    }

    renderZones() {
        const { zone, zones } = this.props;
        const options = this.getItemsAsOptions(zones);

        if (!zones.length) return null;
        return (
            <div className={styles.zones}>
                <Title className={styles.title} size='lg'>
                    {_('Zonas')}
                </Title>
                <div className={styles.select}>
                    <Select
                        appearance='outline'
                        value={zone ? zone.id : options?.[0]?.id}
                        options={options}
                        onChange={this.props.setZone}
                        placeholder={_('Seleccionar zona')}
                        clearable={false}
                        color={settings.app.theme}
                        maxHeight='200px'
                    />
                </div>
            </div>
        );
    }

    renderProductSelection() {
        const { brand } = this.props;
        const title = (
            <Fragment>
                <Title className={styles.title} size='lg'>
                    {_('Selección de productos')}
                </Title>
                <Title className={styles.subtitle} size='md' color='light'>
                    {_('Seleccioná que híbridos queres ver en la ficha técnica de producto.')}
                </Title>
            </Fragment>
        );

        return (
            <Box sx={{ borderBottom: (t) => `2px solid ${t.palette.primary.main}`, mb: 2 }}>
                <ProductList
                    title={title}
                    className={styles.list}
                    profiles={this.props.products}
                    onProductChange={this.handleProductChange}
                    metrics={this.props.metricChoices}
                    brand={brand}
                    type={this.props.type}
                />
            </Box>
        );
    }

    renderModulesSelection() {
        const { brand, selectedIds } = this.props;
        if (!selectedIds.length) return null;
        const defaultCheck = (profile, key) => !!(profile[key] || []).length;
        const densityCheck = (profile) => {
            if (!((profile.environmentRanges || []).length || profile.densityTableConstraints)) return false;
            return true;
        };

        // const headToHeadCheck = (profile) => {
        //     if (!profile.agronomicZones.length > 0) return false;
        //     if (!profile.profileHeadToHeadComparisons.length > 0) return false;
        //     if (!profile.yieldHeadToHead || profile.yieldHeadToHead.length === 0) return false;
        //     return true;
        // };

        // TO-DO: Move logic of available modules to redux
        const options = [
            { key: 'densities', name: _('Rendimiento y Densidad'), check: densityCheck },
            { key: 'metrics', name: _('Seleção de doenças'), check: defaultCheck },
            { key: 'features', name: _('Características'), check: defaultCheck },
            { key: 'refugeOptions', name: _('Refugio'), check: defaultCheck },
            // { key: 'headToHead', name: _('Head to head'), check: headToHeadCheck },
        ].filter((option) =>
            this.state.selectedProfiles.some((profile) => {
                return option.check(profile, option.key);
            })
        );

        return (
            <Box sx={{ borderBottom: (t) => `2px solid ${t.palette.primary.main}`, mb: 2 }}>
                <Title className={styles.title} size='lg'>
                    {_('Selección de módulos')}
                </Title>
                <Title className={styles.subtitle} size='md' color='light'>
                    {_('Seleccioná qué modulos queres ver en la ficha técnica de producto.')}
                </Title>
                <Modules brand={brand} type={'modules'} options={options} />
            </Box>
        );
    }

    renderExtrasSelection() {
        const { brand, selectedIds, productDatasheet } = this.props;
        const extras = 'extras';
        if (!selectedIds.length) return null;
        const options = [
            { key: 'openingImage', name: _('Cover') },
            {
                key: 'brandValues',
                name: _('Pilares de la marca'),
                check: (pd) => Boolean(pd.config?.brandValues?.length),
            },
            { key: 'closingImage', name: _('Closing') },
        ].filter((option) => !option.check || option.check(productDatasheet));

        return (
            <Box sx={{ borderBottom: (t) => `2px solid ${t.palette.primary.main}`, mb: 2 }}>
                <Title className={styles.title} size='lg'>
                    {_('Extras')}
                </Title>
                <Modules brand={brand} type={extras} options={options} />
            </Box>
        );
    }

    renderFeatures() {
        const { brand, productDatasheet } = this.props;
        const features = 'features';
        const options = this.state.selectedProfiles.reduce((acc, profile) => {
            profile.features.forEach((item) => {
                if (!acc.some((feature) => feature.key === item.id)) {
                    acc.push({
                        name: _(item.name),
                        key: item.id,
                    });
                }
            });
            return acc;
        }, []);
        if (!options) {
            productDatasheet.modulesIds.filter((module) => {
                module !== features;
            });
            return null;
        }
        productDatasheet.modulesIds.push(features);
        return (
            <Box sx={{ borderBottom: (t) => `2px solid ${t.palette.primary.main}`, mb: 2 }}>
                <Title className={styles.title} size='lg'>
                    {_('Selección de características')}
                </Title>
                <Title className={styles.subtitle} size='md' color='light'>
                    {_('Seleccioná qué características queres ver en la ficha técnica de producto.')}
                </Title>
                <Modules brand={brand} type={features} options={options} />
            </Box>
        );
    }

    renderMetrics() {
        const { brand, productDatasheet } = this.props;
        const metrics = 'metrics';
        const options = this.state.selectedProfiles.reduce((acc, profile) => {
            profile.metrics.forEach((item) => {
                if (!acc.some((metric) => metric.key === item.id)) {
                    acc.push({
                        name: _(item.name),
                        key: item.id,
                    });
                }
            });
            return acc;
        }, []);
        if (!options) {
            productDatasheet.modulesIds.filter((module) => {
                module !== metrics;
            });
            return null;
        }
        productDatasheet.modulesIds.push(metrics);
        return (
            <Box sx={{ borderBottom: (t) => `2px solid ${t.palette.primary.main}`, mb: 2 }}>
                <Title className={styles.title} size='lg'>
                    {_('Selección de perfil defensivo')}
                </Title>
                <Title className={styles.subtitle} size='md' color='light'>
                    {_('Seleccioná qué perfil defensivo queres ver en la ficha técnica de producto.')}
                </Title>
                <Modules brand={brand} type={metrics} options={options} />
            </Box>
        );
    }

    handleSubmitProductDatasheet() {
        this.context.history(reverse('productDatasheet.ProductDatasheetPreview'));
    }

    renderActions() {
        return (
            <div className={styles.actions}>
                <Button
                    variant='contained'
                    onClick={this.handleSubmitProductDatasheet}
                    disabled={!this.props.productDatasheet.selectedIds.length}
                >
                    {_('Ver ficha técnica de producto')}
                </Button>
            </div>
        );
    }

    render() {
        return (
            <Fragment>
                <PrivacyBar className={styles.privacyBar} />
                <div className={styles.root}>
                    <div className={styles.headline}>
                        <Title className={styles.title} size='xl'>
                            {_('Ficha técnica de producto')}
                        </Title>
                        {!!this.props.products.length && (
                            <Title className={styles.subtitle} size='md' color='light'>
                                {_('Indicá tus variables para obtener una ficha técnica de producto.')}
                            </Title>
                        )}
                    </div>
                    {this.props.loading ? (
                        <Grid container flexDirection='column'>
                            {Array.from(new Array(4)).map((_, index) => (
                                <Grid item key={index}>
                                    <Skeleton variant='rectangular' sx={{ mb: 2 }} width='100%' height={250} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : this.props.products.length ? (
                        <Fragment>
                            {this.renderZones()}
                            <Fragment>
                                {this.renderProductSelection()}
                                {this.renderModulesSelection()}
                                {this.renderExtrasSelection()}
                                {this.renderFeatures()}
                                {this.renderMetrics()}
                                {this.renderActions()}
                            </Fragment>
                        </Fragment>
                    ) : (
                        <div>
                            <Title className={styles.subtitle} size='md' color='light'>
                                {_('No se encontraron productos para la ficha técnica de producto.')}
                            </Title>
                        </div>
                    )}
                </div>
            </Fragment>
        );
    }
}

export default HookWrapper(ProductDatasheetProductsConfiguration);
